import { useMutation } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import {
  createOrderingColumn,
  updateOrderingColumn,
} from '@/data/services/question/orderingColumnServices';
import {
  createOrderingQuestion,
  updateOrderingQuestion,
} from '@/data/services/question/orderingQuestionServices';
import Activity from '@/models/Activity';
import { FlowColumn } from '@/models/DNDOrdenation';
import { DNDOrdenationQuestion } from '@/models/Question';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { adapterOrderingAnswers } from '@/utils/questions/adapterAnswersDTO';
import {
  adapterFeedbacks,
  sanitizeFeedbacks,
} from '@/utils/questions/adapterFeedbacks';
import { sanitizeOrderingAnswers } from '@/utils/questions/sanitizeAnswers';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import alert from '@/utils/shared/alert';
import OrderingForm, { OrderingFormFields } from './OrderingForm';

type OrderingFormServiceProps = {
  activity: Activity;
  question: DNDOrdenationQuestion;
  isActive?: boolean;
};

export default function OrderingFormService({
  activity,
  question,
  isActive,
}: OrderingFormServiceProps) {
  const gradeCtx = useQuestionExamGradeCtx();
  const column = question.columns?.at(0);

  const handleCreate = async (
    data: OrderingFormFields,
  ): Promise<DNDOrdenationQuestion> => {
    const { maxGrade, ...rest } = data;
    const createdActivity = await createOrderingQuestion({
      activity: activity.id,
      type: question.type,
      identifier: question.identifier,
      order: question.order,
      title: rest.title,
      context: rest.context,
      feedbacks: sanitizeFeedbacks(rest.feedbacks),
    });

    const createdColumn = await createOrderingColumn({
      dndOrdenationActivity: createdActivity.id,
      name: 'default',
      flow: rest.flow,
      items: sanitizeOrderingAnswers(rest.orderingItems),
    });

    const saved = { ...createdActivity, columns: [createdColumn] };
    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const handleUpdate = async (
    data: OrderingFormFields,
  ): Promise<DNDOrdenationQuestion> => {
    const { maxGrade, ...rest } = data;
    const oldValues: Omit<OrderingFormFields, 'maxGrade'> = {
      title: normalizeTranslatableField(question.title),
      context: normalizeTranslatableField(question.context),
      flow: (column?.flow as FlowColumn) ?? FlowColumn.VERTICAL,
      orderingItems: adapterOrderingAnswers(column?.items ?? []),
      feedbacks: adapterFeedbacks(question.feedbacks ?? []),
    };

    const activityChanges: Partial<
      Pick<OrderingFormFields, 'title' | 'context' | 'feedbacks'>
    > = {};
    if (!isEqual(oldValues.title, rest.title)) {
      activityChanges.title = rest.title;
    }
    if (!isEqual(oldValues.context, rest.context)) {
      activityChanges.context = rest.context;
    }
    const newSanitizedFeedbacks = sanitizeFeedbacks(rest.feedbacks);
    const oldSanitizedFeedbacks = sanitizeFeedbacks(oldValues.feedbacks);
    if (!isEqual(oldSanitizedFeedbacks, newSanitizedFeedbacks)) {
      activityChanges.feedbacks = newSanitizedFeedbacks;
    }

    const columnChanges: Record<string, unknown> = {};
    if (!isEqual(oldValues.flow, rest.flow)) {
      columnChanges.flow = rest.flow;
    }
    const oldSanitizedOrderingItems = sanitizeOrderingAnswers(
      oldValues.orderingItems,
    );
    const newSanitizedOrderingItems = sanitizeOrderingAnswers(
      rest.orderingItems,
    );
    if (!isEqual(oldSanitizedOrderingItems, newSanitizedOrderingItems)) {
      columnChanges.items = newSanitizedOrderingItems;
    }

    const hasActivityChanges = Object.keys(activityChanges).length > 0;
    const hasColumnChanges = Object.keys(columnChanges).length > 0;

    let saved: DNDOrdenationQuestion = question;

    if (hasActivityChanges || hasColumnChanges) {
      let updatedActivity = question;

      if (hasActivityChanges) {
        updatedActivity = await updateOrderingQuestion(
          question.id,
          activityChanges,
        );
      }

      if (hasColumnChanges && column?.id) {
        const updatedColumn = await updateOrderingColumn(column.id, {
          ...(columnChanges.flow !== undefined && {
            flow: columnChanges.flow as FlowColumn,
          }),
          ...(columnChanges.items !== undefined && {
            items: columnChanges.items as typeof rest.orderingItems,
          }),
        });

        saved = { ...updatedActivity, columns: [updatedColumn] };
      } else {
        saved = updatedActivity;
      }
    }

    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: (data: OrderingFormFields) =>
      !question.id ? handleCreate(data) : handleUpdate(data),
    onError: err => alert.error(getErrorMessage(err)),
  });

  return (
    <OrderingForm
      key={question.identifier}
      isActive={isActive}
      question={question}
      onSubmit={submitQuestion}
      isLoading={isPending}
    />
  );
}
